import { adminApi, ensureArray } from '../services/api';
import { useState, useEffect } from 'react';

interface CampusPartnership {
  id: string;
  universityName: string;
  emailDomain: string;
  subsidisedRateNgn: number;
  monthlyInvoice: number;
  active: boolean;
  createdAt: string;
}

export function CampusPartnershipsPage() {
  const [partnerships, setPartnerships] = useState<CampusPartnership[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    universityName: '',
    emailDomain: '',
    subsidisedRateNgn: '40',
    monthlyInvoice: '0',
  });

  const load = () => {
    setLoading(true);
    adminApi<CampusPartnership[]>('/admin/campus-partnerships')
      .then((d) => setPartnerships(ensureArray(d)))
      .catch(() => setPartnerships([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await adminApi('/admin/campus-partnerships', {
        method: 'POST',
        body: JSON.stringify({
          universityName: form.universityName,
          emailDomain: form.emailDomain,
          subsidisedRateNgn: parseInt(form.subsidisedRateNgn, 10),
          monthlyInvoice: parseInt(form.monthlyInvoice, 10),
          active: true,
        }),
      });
      setForm({
        universityName: '',
        emailDomain: '',
        subsidisedRateNgn: '40',
        monthlyInvoice: '0',
      });
      load();
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (id: string, active: boolean) => {
    await adminApi(`/admin/campus-partnerships/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ active: !active }),
    });
    load();
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Campus Partnerships</h1>
        <p className="mt-0.5 text-sm text-gray-500">
          Manage university email domains and student subsidy rates
        </p>
      </div>

      <form
        onSubmit={create}
        className="mb-8 max-w-lg rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
      >
        <h2 className="mb-4 font-semibold text-gray-900">Add Partnership</h2>
        <div className="space-y-3">
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="University name"
            value={form.universityName}
            onChange={(e) => setForm((f) => ({ ...f, universityName: e.target.value }))}
            required
          />
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="Email domain (e.g. unilag.edu.ng)"
            value={form.emailDomain}
            onChange={(e) => setForm((f) => ({ ...f, emailDomain: e.target.value }))}
            required
          />
          <div className="flex gap-3">
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              type="number"
              min={1}
              max={99}
              placeholder="Subsidy %"
              value={form.subsidisedRateNgn}
              onChange={(e) => setForm((f) => ({ ...f, subsidisedRateNgn: e.target.value }))}
              required
            />
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              type="number"
              min={0}
              placeholder="Monthly invoice ₦"
              value={form.monthlyInvoice}
              onChange={(e) => setForm((f) => ({ ...f, monthlyInvoice: e.target.value }))}
            />
          </div>
          <button
            type="submit"
            disabled={creating}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create Partnership'}
          </button>
        </div>
      </form>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : partnerships.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center text-gray-400">
          No campus partnerships yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="border-b bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">University</th>
                <th className="px-4 py-3">Domain</th>
                <th className="px-4 py-3">Subsidy</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {partnerships.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-3 font-medium">{p.universityName}</td>
                  <td className="px-4 py-3 text-gray-500">@{p.emailDomain}</td>
                  <td className="px-4 py-3">{p.subsidisedRateNgn}%</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${p.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {p.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleActive(p.id, p.active)}
                      className="text-indigo-600 hover:underline"
                    >
                      {p.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
